import React, { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * Abstract search-reach diagram in a 960x300 viewBox. Not a literal map.
 * `region` indexes into copy.geography.regions:
 * 0 Ukraine · 1 Europe · 2 United Kingdom · 3 USA · 4 Worldwide
 */
const POINTS = [{
  x: 120,
  y: 196,
  key: 'usa',
  region: 3
}, {
  x: 400,
  y: 96,
  key: 'uk',
  region: 2
}, {
  x: 520,
  y: 178,
  key: 'europe',
  region: 1
}, {
  x: 640,
  y: 128,
  key: 'ukraine',
  region: 0,
  anchor: true
}, {
  x: 840,
  y: 214,
  key: 'worldwide',
  region: 4
}] as const;
const ARCS = [[3, 2], [3, 1], [3, 4], [2, 0], [2, 4]] as const;
function arcPath(a: {
  x: number;
  y: number;
}, b: {
  x: number;
  y: number;
}): string {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2 - Math.abs(b.x - a.x) * 0.22;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
}
export function GeographySection() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="bg-sand py-20 lg:py-28 overflow-hidden" ref={ref} data-id="element-399" data-mp="3c508cl6wqekw">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8" data-id="element-400" data-mp="3c731vf79qpud">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.55,
        ease: EASE
      }} className="max-w-[42rem]" data-id="element-401" data-mp="3q87yp8g8fsf">
          <h2 className="esca-h2 text-ink text-balance" data-id="element-402" data-mp="3q48btqa3s8uw">
            {c.geography.title}
          </h2>
          <p className="esca-lead mt-5 text-muted max-w-[48ch]" data-id="element-403" data-mp="3v21j6mn578wr">
            {c.geography.lead}
          </p>
        </motion.div>

        <div className="mt-14 lg:mt-16" data-id="element-404" data-mp="rerdwy410ghc">
          <svg viewBox="0 0 960 300" className="w-full h-auto" role="img" aria-label={c.geography.regions.join(', ')} data-id="element-405" data-mp="rft120ybgtff">
            {ARCS.map(([a, b], i) => <motion.path key={`arc-${i}`} d={arcPath(POINTS[a], POINTS[b])} fill="none" stroke="#C3CBC7" strokeWidth="1" initial={reduce ? false : {
            pathLength: 0,
            opacity: 0
          }} animate={inView ? {
            pathLength: 1,
            opacity: 1
          } : {}} transition={{
            duration: 0.7,
            delay: 0.25 + i * 0.1,
            ease: EASE
          }} data-id="element-406" data-mp="wc6k5grp74y1" data-mpi={i} />)}

            {POINTS.map((p, i) => <motion.g key={p.key} initial={reduce ? false : {
            opacity: 0
          }} animate={inView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.35,
            delay: 0.15 + i * 0.08,
            ease: EASE
          }} data-id="element-407" data-mp="4la9mmt41cdx" data-mpi={i}>
                {p.anchor && <circle cx={p.x} cy={p.y} r="13" fill="none" stroke="#FF6846" strokeWidth="1" strokeOpacity="0.35" data-id="element-408" data-mp="2b8rbjf6jc8s6" />}
                <circle cx={p.x} cy={p.y} r={p.anchor ? 5 : 3.5} fill={p.anchor ? '#FF6846' : '#66716D'} data-id="element-409" data-mp="1nj5fh0lgm9oe" />
                <text x={p.x} y={p.y - (p.anchor ? 24 : 16)} textAnchor="middle" className="fill-ink" style={{
              fontFamily: "'Inter Tight', Inter, sans-serif",
              fontSize: p.anchor ? 20 : 17,
              fontWeight: p.anchor ? 700 : 500,
              letterSpacing: '-0.02em'
            }} data-id="element-410" data-mp="1sfvwpr903bbe">
                  {c.geography.regions[p.region]}
                </text>
              </motion.g>)}
          </svg>
        </div>
      </div>
    </section>;
}